import { isEmpty } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import type { Search } from "react-router";

import {
  ENVIRONMENT_PRODUCTION,
  getAuthId,
  getAuthIdFromConfig,
  IntegrationAuthType,
  isExchangeCodeResponse,
} from "@superblocksteam/shared";
import type {
  Agent,
  AuthConfig,
  AuthId,
  AuthType,
  DatasourceAuthState,
  DatasourceOneTimeState,
  Organization,
  Profile,
  SupersetIntegrationDto,
  ViewMode,
} from "@superblocksteam/shared";

import { useFeatureFlag } from "hooks/ui/useFeatureFlag";
import { getAppViewMode } from "legacy/selectors/applicationSelectors";
import localStorage from "legacy/utils/localStorage";
import { selectActiveAgents } from "store/slices/agents";
import {
  exchangeCodeV3,
  loginDatasourceV3,
} from "store/slices/apisShared/client-auth";
import { Flag } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations";
import { orgIsOnPremise } from "store/slices/organizations/utils";
import logger from "utils/logger";

import {
  resolveOAuthCallbackConfig,
  type ResolvedDatasourceConfig,
} from "./oauth-callback-config";
import {
  exchangeOAuthCallbackCode,
  fetchOAuthCallbackIntegration,
  requestUnpartitionedCookies,
} from "./OAuthCallback.utils";
import {
  LS_FULL_STATE,
  LS_OAUTH_ACCESS_TOKEN,
  LS_OAUTH_DATASOURCE_KEY,
  LS_OAUTH_ENV_KEY,
  LS_OAUTH_ERROR_KEY,
  LS_OAUTH_ONE_TIME_CODE,
  LS_OAUTH_REDIRECT_FINISHED,
} from "./OAuthRedirectLoginModal";

// This component is used to handle the OAuth callback from the OAuth provider(for new/existing Superblocks-managed OAuth clients and for new bring your own OAuth clients).
// For bring your own OAuth clients:
// - It will exchange the code for an access token and then close the window. The same can't be done for Superblocks-managed OAuth clients because we don't want to expose the client secret to the browser.
// For Superblocks-managed OAuth clients:
// - It will just close the window and update the local storage with status and error if any.
const OAuthCallback = () => {
  // State
  const location = useLocation();
  const hash = location.hash;
  const fragments = new URLSearchParams(hash.slice(1));

  // Either the token or the access code will be provided depending on the code
  // that was requested.
  const token = fragments.get("access_token");
  const expiresIn = fragments.get("expires_in");
  const expiryTimestamp = useMemo(() => {
    if (expiresIn) {
      return new Date().getTime() + parseInt(expiresIn) * 1000;
    }
    return undefined;
  }, [expiresIn]);

  const accessCode = new URLSearchParams(location.search).get("code");
  const paramError = new URLSearchParams(location.search).get("error");
  const authState = useMemo(
    () => getStateFromLocalOrParam(location.search),
    [location.search],
  );
  const scope = new URLSearchParams(location.search).get("scope");
  const organization = useSelector(selectOnlyOrganization);
  // TODO DURING REVIEW: Why does this always use production agents?
  const datasourceToAuth = authState.integrationId;
  const environment =
    localStorage.getItem(LS_OAUTH_ENV_KEY) ?? ENVIRONMENT_PRODUCTION;
  const oauthAccessToken = authState.externalUser
    ? (localStorage.getItem(LS_OAUTH_ACCESS_TOKEN) ?? undefined)
    : undefined;
  const needsStorageAccess = authState.externalUser && !oauthAccessToken;
  const agents: Agent[] = useSelector(
    selectActiveAgents(organization?.agentType, environment),
  );

  const profile = organization?.profiles?.find((p) => p.key === environment);
  const profileId = useMemo(() => {
    return profile?.id ?? "";
  }, [profile?.id]);

  const [errorMessage, setErrorMessage] = useState<string>();
  const [isLoading, setIsLoading] = useState(true);
  // Shell-driven embed popups pass an explicit runtime token through
  // same-origin localStorage, so they do not need unpartitioned cookie access.
  // Keep the Storage Access fallback for older embed paths that still rely on
  // the host-only `embed-token` cookie.
  const [storageAccess, setStorageAccess] = useState<
    "pending" | "granted" | "denied"
  >("pending");
  // To prevent multiple requests from being sent in case the main useEffect is triggered multiple times.
  const sentRequest = useRef(false);
  const mode = useSelector(getAppViewMode);
  const enableDataPlaneGateway = useFeatureFlag(Flag.ENABLE_DATA_PLANE_GATEWAY);

  const agentsLoading = useMemo(() => {
    const needsAgents = () => {
      // A helper to determine if we need agent URLs.
      // If control flow is disabled, we make agent requests to a known
      // address. No discovery needed.
      return orgIsOnPremise(organization);
    };

    // Agents are loading if we need them and they're not there.
    return needsAgents() && isEmpty(agents);
  }, [agents, organization]);

  // finish sets all of the variables to communicate to the other tab that it's
  // done.
  const finish = useCallback((error?: string) => {
    setIsLoading(false);
    localStorage.setItem(LS_OAUTH_REDIRECT_FINISHED, "true");
    localStorage.removeItem(LS_FULL_STATE);
    localStorage.removeItem(LS_OAUTH_ACCESS_TOKEN);
    // Errors: keep the popup open so the provider's verbatim message
    // (e.g. "access_denied: User denied the authorization request" or a
    // 401 from the integration fetch) stays visible to the user. The
    // opener still gets the failure signal via LS_OAUTH_REDIRECT_FINISHED
    // + LS_OAUTH_ERROR_KEY -- this is set above before we return -- so
    // the host UI can show its own banner while the user reads the
    // popup. Tested by `embedding-oauth-error.spec.ts` and
    // `embedding-oauth-error-fullstack.spec.ts`.
    if (error) {
      return;
    }

    // NOTE that this just closes the window when the window has been
    // automatically opened.  If a user copies the OAuth URL and manually opens
    // it in a new tab, this will not close the window.
    if (localStorage.getItem("sb_debug_stop_auto_close")) {
      return;
    }

    window.close();
  }, []);

  const errorCaseAndFinish = useCallback(
    (error: any) => {
      logger.warn(`Login Error: ${error}`);
      setErrorMessage(error);
      localStorage.setItem(
        LS_OAUTH_ERROR_KEY,
        `Authentication failed: ${error}`,
      );
      // Done, there was an error
      finish(error);
    },
    [finish],
  );

  // Clear the error message (from previous attempts perhaps) on mount.
  useEffect(() => {
    localStorage.removeItem(LS_OAUTH_ERROR_KEY);
  }, []);

  // Only the legacy cookie-authenticated embed callback path needs Storage
  // Access. Shell-driven popups use `oauthAccessToken` for their same-origin
  // callback requests.
  useEffect(() => {
    if (!needsStorageAccess) {
      setStorageAccess("granted");
      return;
    }
    let cancelled = false;
    (async () => {
      const { granted } = await requestUnpartitionedCookies();
      if (cancelled) return;
      setStorageAccess(granted ? "granted" : "denied");
    })();
    return () => {
      cancelled = true;
    };
  }, [needsStorageAccess]);

  // Load the relevant datasource auth config async.
  const [datasource, setDatasource] = useState<
    SupersetIntegrationDto | undefined
  >();
  const dsConfig = useMemo(() => {
    return resolveOAuthCallbackConfig({
      datasource,
      profileId,
      authState,
    });
  }, [datasource, profileId, authState]);

  const [dsLoadError, setDsLoadError] = useState<string>();
  const [dsLoadState, setDsLoadState] = useState<
    "idle" | "loading" | "loaded" | "error"
  >("idle");
  useEffect(() => {
    // Wait for Storage Access only when this callback still depends on the
    // cookie fallback; explicit bearer auth can load the integration
    // immediately.
    if (needsStorageAccess && storageAccess === "pending") return;
    const loadDS = async () => {
      setDsLoadState("loading");
      try {
        const datasource = await fetchOAuthCallbackIntegration(
          datasourceToAuth,
          profile?.key,
          oauthAccessToken,
        );
        if (datasource) {
          setDsLoadError(undefined);
          setDatasource(datasource);
          setDsLoadState("loaded");
        } else {
          setDsLoadError("Integration request did not return a datasource.");
          setDsLoadState("error");
        }
      } catch (err) {
        logger.error(`Failed to load integration: ${err}`);
        setDsLoadError(String(err));
        setDsLoadState("error");
      }
    };
    loadDS();
  }, [
    datasourceToAuth,
    needsStorageAccess,
    oauthAccessToken,
    profile?.key,
    storageAccess,
  ]);

  const loginWithTokenAndFinish = useCallback(
    async (dsConfig: ResolvedDatasourceConfig) => {
      // Login with token.
      const authId = getAuthIdFromConfig(datasourceToAuth, dsConfig);
      await loginDatasourceV3(
        profile && datasourceToAuth // fallback to controller if orch reqs arent met
          ? {
              orchestrator: true,
              integrationId: datasourceToAuth,
              profile,
              token: token ?? undefined,
              expiryTimestamp,
              agents,
              organization,
              enableDataPlaneGateway,
              profileKey: profile?.key,
            }
          : {
              orchestrator: false,
              agents,
              organization,
              body: {
                authType: IntegrationAuthType.OAUTH2_IMPLICIT,
                authId,
                token,
                expiryTimestamp,
              },
            },
      );
      finish();
    },
    [
      agents,
      datasourceToAuth,
      enableDataPlaneGateway,
      expiryTimestamp,
      finish,
      organization,
      profile,
      token,
    ],
  );

  const loginWithAuthCodeAndFinish = useCallback(
    async (dsConfig: ResolvedDatasourceConfig) => {
      // Login with access code.
      try {
        if (validateOneTimeCodeIfApplicable(authState, dsConfig.authConfig)) {
          const redirectInitiatedByEmbedUser = authState.externalUser;
          const useServerEndpoint =
            dsConfig.authConfig?.refreshTokenFromServer ||
            redirectInitiatedByEmbedUser;
          const authId = getAuthId(
            dsConfig.authType,
            dsConfig.authConfig,
            dsConfig.integrationId,
            dsConfig.configurationId,
          );
          if (
            authState.useLocalStorage &&
            localStorage.getItem(LS_OAUTH_REDIRECT_FINISHED) === "true"
          ) {
            // Request has already been made.
            return;
          }
          const integrationId = redirectInitiatedByEmbedUser
            ? dsConfig.integrationId
            : undefined;
          const configurationId = redirectInitiatedByEmbedUser
            ? dsConfig.configurationId
            : undefined;
          const exchangeCodeResp = useServerEndpoint
            ? await doExchangeCodeOnServer(
                authId,
                dsConfig.authType,
                dsConfig.authConfig,
                accessCode as string,
                dsConfig.pluginId,
                scope,
                integrationId,
                configurationId,
                oauthAccessToken,
              )
            : await doExchangeCodeOnAgent(
                dsConfig.integrationId,
                profile,
                accessCode as string,
                agents,
                organization,
                datasource,
                environment,
                dsConfig,
                mode,
                enableDataPlaneGateway,
                profile?.key,
              );
          if (
            (isExchangeCodeResponse(exchangeCodeResp) &&
              !exchangeCodeResp.successful) ||
            (!isExchangeCodeResponse(exchangeCodeResp) &&
              !exchangeCodeResp.success)
          ) {
            localStorage.setItem(
              LS_OAUTH_ERROR_KEY,
              `Failed to exchange an authorization code for an access token: ${exchangeCodeResp.error}.`,
            );
          }
        } else {
          localStorage.setItem(
            LS_OAUTH_ERROR_KEY,
            `Failed to exchange an authorization code for an access token. One time code in state parameter did not match.`,
          );
        }
      } catch (err) {
        localStorage.setItem(
          LS_OAUTH_ERROR_KEY,
          `Failed to exchange an authorization code for an access token: ${err}.`,
        );
      }
      finish();
    },
    [
      accessCode,
      agents,
      datasource,
      enableDataPlaneGateway,
      environment,
      finish,
      mode,
      organization,
      profile,
      oauthAccessToken,
      scope,
      authState,
    ],
  );

  // THIS IS THE MAIN ENTRY POINT
  useEffect(() => {
    (async () => {
      if (paramError) {
        // Check for error cases that need to be propagated from earlier in the
        // flow.
        return errorCaseAndFinish(paramError);
      }

      // Older embed paths may still authenticate the callback through the
      // embed-token cookie. Shell-driven popups pass `oauthAccessToken` and do
      // not need Storage Access.
      if (needsStorageAccess && storageAccess === "pending") {
        return;
      }

      if (needsStorageAccess && storageAccess === "denied") {
        return errorCaseAndFinish(
          `Your browser blocked third-party cookies for ${window.location.host}. ` +
            `Please allow cookies for this site, or disable strict / incognito ` +
            `tracking protection, and try again.`,
        );
      }

      if (authState.useLocalStorage && !localStorage.isSupported()) {
        return errorCaseAndFinish(
          `This integration requires local storage to be enabled. Please enable local storage and try again.`,
        );
      }

      // Check that all of the dependencies are loaded. Return will wait for
      // them to load (async).
      if (agentsLoading) {
        logger.info("No agents found.");
        return;
      } else if (dsLoadError && !dsConfig) {
        return errorCaseAndFinish(
          `Failed to load integration configuration: ${dsLoadError}`,
        );
      } else if (!dsConfig) {
        if (datasource) {
          return errorCaseAndFinish(
            "Integration loaded but no matching configuration was found.",
          );
        }
        if (dsLoadState === "idle" || dsLoadState === "loading") {
          logger.info("Datasource not found");
          return;
        }
        return errorCaseAndFinish("Failed to load integration configuration.");
      }

      // Attempt to send the request but another request already sent or the redirect has already finished, no need to send another request.
      if (
        sentRequest.current ||
        (authState.useLocalStorage &&
          localStorage.getItem(LS_OAUTH_REDIRECT_FINISHED) === "true")
      ) {
        return;
      }

      sentRequest.current = true;
      if (!isEmpty(token)) {
        await loginWithTokenAndFinish(dsConfig);
      } else if (!isEmpty(accessCode)) {
        await loginWithAuthCodeAndFinish(dsConfig);
      } else {
        errorCaseAndFinish("Expected either an access token or code.");
      }
    })();
  }, [
    token,
    accessCode,
    authState,
    paramError,
    agentsLoading,
    datasource,
    dsConfig,
    dsLoadError,
    dsLoadState,
    errorCaseAndFinish,
    loginWithTokenAndFinish,
    loginWithAuthCodeAndFinish,
    needsStorageAccess,
    storageAccess,
  ]);

  // If there is an error to show, we don't close this screen so we prioritize
  // showing that.
  return (
    <>
      {errorMessage ??
        (isLoading
          ? "Logging in..."
          : "Login complete. This window can be closed.")}
    </>
  );
};

async function doExchangeCodeOnServer(
  authId: AuthId,
  authType: AuthType,
  authConfig: AuthConfig,
  accessCode: string,
  pluginId: string,
  scope: string | null,
  integrationId: string | undefined,
  configurationId: string | undefined,
  accessToken: string | undefined,
) {
  return await exchangeOAuthCallbackCode(
    {
      authId: authId,
      authType: authType,
      authConfig: authConfig,
      accessCode: accessCode,
      pluginId: pluginId,
      origin: window.location.origin,
      grantedScope: scope,
      integrationId,
      configurationId,
    },
    accessToken,
  );
}

async function doExchangeCodeOnAgent(
  integrationId: string,
  profile: Profile | undefined,
  accessCode: string,
  agents: Agent[],
  organization: Organization,
  datasource: any,
  environment: string,
  dsConfig: ResolvedDatasourceConfig,
  mode: ViewMode | undefined,
  enableDataPlaneGateway?: boolean,
  profileKey?: string,
) {
  return await exchangeCodeV3(
    integrationId && profile && accessCode
      ? {
          orchestrator: true,
          agents,
          organization,
          integrationId:
            dsConfig.configSource === "server" ? datasource?.id : undefined,
          configurationId: dsConfig.configurationId,
          accessCode,
          profile,
          authType: dsConfig.authType,
          authConfig: dsConfig.authConfig,
          enableDataPlaneGateway,
          profileKey,
        }
      : {
          orchestrator: false,
          agents,
          organization,
          body: {
            accessCode,
            datasourceId: datasource?.id,
            environment: environment,
            authType: dsConfig.authType,
            authConfig: dsConfig.authConfig,
          },
          mode,
          profile,
        },
  );
}

function validateOneTimeCodeIfApplicable(
  state: DatasourceOneTimeState,
  authConfig: AuthConfig,
): boolean {
  if (!authConfig.sendOAuthState) {
    return true;
  }
  if (!state) {
    return false;
  }
  if (!state.useLocalStorage) {
    //TODO: find another way to validate one time code
    return true;
  }
  return state.oneTimeCode === localStorage.getItem(LS_OAUTH_ONE_TIME_CODE);
}

function getStateFromLocalOrParam(
  search: Search,
): DatasourceOneTimeState | DatasourceAuthState {
  const fullStateFromLocalStorage = localStorage.getItem(LS_FULL_STATE);
  const stateValue =
    fullStateFromLocalStorage ?? new URLSearchParams(search).get("state");
  if (stateValue) {
    try {
      const authState: DatasourceOneTimeState = JSON.parse(atob(stateValue));
      return authState;
    } catch {
      logger.warn(`Failed to parse state string`);
    }
  }
  // fallback to local storage
  return {
    oneTimeCode: localStorage.getItem(LS_OAUTH_ONE_TIME_CODE) ?? "",
    useLocalStorage: true,
    integrationId: localStorage.getItem(LS_OAUTH_DATASOURCE_KEY) ?? "",
    externalUser: false,
  };
}

export default OAuthCallback;
